import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { 
  User, 
  Phone, 
  Mail, 
  Calendar, 
  MapPin,
  Edit,
  Save
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import HomeroomSelect from './HomeroomSelect';

const TeacherDetailsDialog = ({ teacher, open, onClose, onUpdate }) => {
  const [loading, setLoading] = useState(false);
  const [editMode, setEditMode] = useState(false);
  
  // Teacher form state
  const [teacherForm, setTeacherForm] = useState({
    full_name: '',
    homeroom_grade: null,
    assigned_grades: [],
    subjects: [],
    phone: '',
    address: '',
    hire_date: ''
  });

  const grades = ['Preescolar', 'Kinder', '1ro', '2do', '3ro', '4to', '5to', '6to', '7mo', '8vo', '9no', '10mo', '11vo', '12vo'];
  const defaultSubjects = ['Matemáticas', 'Español', 'Ciencias', 'Estudios Sociales', 'Inglés', 'Educación Física', 'Arte', 'Música'];

  useEffect(() => {
    if (teacher && open) {
      setTeacherForm({
        full_name: teacher.full_name || '',
        homeroom_grade: teacher.homeroom_grade || null,
        assigned_grades: teacher.assigned_grades || [],
        subjects: teacher.subjects || [],
        phone: teacher.phone || '',
        address: teacher.address || '',
        hire_date: teacher.hire_date ? new Date(teacher.hire_date).toISOString().split('T')[0] : ''
      });
    }
  }, [teacher, open]);

  const handleSaveTeacher = async () => {
    setLoading(true);
    try {
      const updateData = {
        ...teacherForm,
        hire_date: teacherForm.hire_date ? new Date(teacherForm.hire_date).toISOString() : null
      };
      
      await axios.put(`/teachers/${teacher.id}`, updateData);
      toast.success('Profesor actualizado exitosamente');
      setEditMode(false);
      onUpdate?.();
    } catch (error) {
      console.error('Error updating teacher:', error);
      toast.error(error.response?.data?.detail || 'Error al actualizar profesor');
    } finally {
      setLoading(false);
    }
  };

  const toggleGrade = (grade) => {
    const isSelected = teacherForm.assigned_grades.includes(grade);
    if (isSelected) {
      setTeacherForm({
        ...teacherForm,
        assigned_grades: teacherForm.assigned_grades.filter(g => g !== grade),
        homeroom_grade: teacherForm.homeroom_grade === grade ? null : teacherForm.homeroom_grade
      });
    } else {
      setTeacherForm({
        ...teacherForm,
        assigned_grades: [...teacherForm.assigned_grades, grade]
      });
    }
  };

  const toggleSubject = (subject) => {
    const isSelected = teacherForm.subjects.includes(subject);
    if (isSelected) {
      setTeacherForm({
        ...teacherForm,
        subjects: teacherForm.subjects.filter(s => s !== subject)
      });
    } else {
      setTeacherForm({
        ...teacherForm,
        subjects: [...teacherForm.subjects, subject]
      });
    }
  };

  if (!teacher) return null;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <User className="w-5 h-5" />
            {teacher.full_name}
          </DialogTitle>
          <DialogDescription>
            Información completa del profesor - ID: {teacher.teacher_number}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          <div className="flex justify-between items-center">
            <h3 className="text-lg font-semibold">Información Personal</h3>
            <Button
              onClick={() => editMode ? handleSaveTeacher() : setEditMode(true)}
              disabled={loading}
              data-testid="edit-teacher-button"
            >
              {editMode ? (
                <>
                  <Save className="w-4 h-4 mr-2" />
                  {loading ? 'Guardando...' : 'Guardar'}
                </>
              ) : (
                <>
                  <Edit className="w-4 h-4 mr-2" />
                  Editar
                </>
              )}
            </Button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="teacher_name">Nombre Completo</Label>
              {editMode ? (
                <Input
                  id="teacher_name"
                  value={teacherForm.full_name}
                  onChange={(e) => setTeacherForm({...teacherForm, full_name: e.target.value})}
                />
              ) : (
                <p className="p-2 bg-gray-50 rounded">{teacher.full_name}</p>
              )}
            </div>

            <div>
              <Label>Número de Profesor</Label>
              <p className="p-2 bg-gray-50 rounded font-mono">{teacher.teacher_number}</p>
            </div>

            <div>
              <Label className="flex items-center gap-2">
                <Phone className="w-4 h-4" />
                Teléfono
              </Label>
              {editMode ? (
                <Input
                  value={teacherForm.phone}
                  onChange={(e) => setTeacherForm({...teacherForm, phone: e.target.value})}
                  placeholder="Número de teléfono"
                />
              ) : (
                <p className="p-2 bg-gray-50 rounded">{teacher.phone || 'No registrado'}</p>
              )}
            </div>

            <div>
              <Label className="flex items-center gap-2">
                <Calendar className="w-4 h-4" />
                Fecha de Contratación
              </Label>
              {editMode ? (
                <Input
                  type="date"
                  value={teacherForm.hire_date}
                  onChange={(e) => setTeacherForm({...teacherForm, hire_date: e.target.value})}
                />
              ) : (
                <p className="p-2 bg-gray-50 rounded">
                  {teacher.hire_date ? new Date(teacher.hire_date).toLocaleDateString() : 'No registrada'}
                </p>
              )}
            </div>
          </div>

          <div>
            <Label className="flex items-center gap-2">
              <MapPin className="w-4 h-4" />
              Dirección
            </Label>
            {editMode ? (
              <Input
                value={teacherForm.address}
                onChange={(e) => setTeacherForm({...teacherForm, address: e.target.value})}
                placeholder="Dirección del profesor"
              />
            ) : (
              <p className="p-2 bg-gray-50 rounded">{teacher.address || 'No registrada'}</p>
            )}
          </div>

          <div>
            <Label>Grados Asignados</Label>
            {editMode ? (
              <div className="grid grid-cols-3 sm:grid-cols-4 gap-2 mt-2">
                {grades.map((grade) => (
                  <label key={grade} className="flex items-center space-x-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={teacherForm.assigned_grades.includes(grade)}
                      onChange={() => toggleGrade(grade)}
                      className="rounded"
                    />
                    <span className="text-sm">{grade}</span>
                  </label>
                ))}
              </div>
            ) : (
              <div className="flex flex-wrap gap-2 mt-2">
                {teacher.assigned_grades?.length > 0 ? (
                  teacher.assigned_grades.map((grade) => (
                    <Badge key={grade} variant="secondary">{grade}</Badge>
                  ))
                ) : (
                  <p className="text-gray-500 italic">Sin grados asignados</p>
                )}
              </div>
            )}
          </div>

          {editMode ? (
            <HomeroomSelect id="teacher-details-homeroom" grades={teacherForm.assigned_grades}
              value={teacherForm.homeroom_grade} onChange={value => setTeacherForm({...teacherForm, homeroom_grade:value})} />
          ) : (
            <div><Label>Salón hogar asignado</Label><p className="p-2 bg-gray-50 rounded">{teacher.homeroom_grade || 'Sin asignar'}</p></div>
          )}

          <div>
            <Label>Materias</Label>
            {editMode ? (
              <div className="grid grid-cols-2 gap-2 mt-2 max-h-40 overflow-y-auto">
                {defaultSubjects.map((subject) => (
                  <label key={subject} className="flex items-center space-x-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={teacherForm.subjects.includes(subject)}
                      onChange={() => toggleSubject(subject)}
                      className="rounded"
                    />
                    <span className="text-sm">{subject}</span>
                  </label>
                ))}
              </div>
            ) : (
              <div className="flex flex-wrap gap-2 mt-2">
                {teacher.subjects?.length > 0 ? (
                  teacher.subjects.map((subject) => (
                    <Badge key={subject} variant="outline">{subject}</Badge>
                  ))
                ) : (
                  <p className="text-gray-500 italic">Sin materias asignadas</p>
                )}
              </div>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4 bg-gray-50 rounded-lg">
            <div className="text-center">
              <p className="text-sm text-gray-600">Estado</p>
              <Badge className={teacher.is_active ? 'status-active' : 'status-inactive'}>
                {teacher.is_active ? 'Activo' : 'Inactivo'}
              </Badge>
            </div>
            <div className="text-center">
              <p className="text-sm text-gray-600">Fecha de Registro</p>
              <p className="text-sm font-medium">
                {new Date(teacher.created_at).toLocaleDateString()}
              </p>
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cerrar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default TeacherDetailsDialog;
